import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Search, ChevronDown, Check, Phone, Mail, MapPin, Wrench, Loader2 } from 'lucide-react'
import { listDemandes, setTraite, type DemandeRow } from '../../api/enlevement'

function formatDate(s: string): string {
  // "2026-01-05 13:34:03" → "05/01/2026"
  const d = s?.slice(0, 10).split('-')
  return d?.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : s
}

type Filtre = 'tous' | 'a_traiter' | 'traites'

export function AdminDemandes() {
  const qc = useQueryClient()
  const { data: demandes = [], isLoading } = useQuery({ queryKey: ['demandes'], queryFn: listDemandes })
  const [search, setSearch] = useState('')
  const [filtre, setFiltre] = useState<Filtre>('tous')
  const [openId, setOpenId] = useState<number | null>(null)

  const mut = useMutation({
    mutationFn: ({ id, traite }: { id: number; traite: boolean }) => setTraite(id, traite),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['demandes'] }),
  })

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return demandes.filter((d) => {
      if (filtre === 'a_traiter' && d.traite) return false
      if (filtre === 'traites' && !d.traite) return false
      if (!q) return true
      return [d.nom, d.localite, d.code_postal, d.numero_demande, d.numero_client, d.email, d.telephone1]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    })
  }, [demandes, search, filtre])

  const nbATraiter = demandes.filter((d) => !d.traite).length

  if (isLoading) {
    return (
      <div className="grid place-items-center py-20 text-ink/50">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  return (
    <div>
      {/* Barre d'outils */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom, localité, n°…)"
            className="w-full rounded-full border border-navy/15 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange"
          />
        </div>
        <div className="flex gap-1.5">
          {([['tous', 'Tous'], ['a_traiter', 'À traiter'], ['traites', 'Traités']] as [Filtre, string][]).map(
            ([k, lbl]) => (
              <button
                key={k}
                onClick={() => setFiltre(k)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  filtre === k ? 'bg-navy text-white' : 'bg-white text-ink/70 ring-1 ring-navy/15 hover:bg-sand'
                }`}
              >
                {lbl}
              </button>
            ),
          )}
        </div>
      </div>

      <p className="mt-4 text-sm text-ink/55">
        {filtered.length} demande{filtered.length > 1 ? 's' : ''} affichée{filtered.length > 1 ? 's' : ''} ·{' '}
        {demandes.length} au total · <span className="font-semibold text-orange">{nbATraiter} à traiter</span>
      </p>

      {/* Liste */}
      <div className="mt-4 space-y-2.5">
        {filtered.map((d) => (
          <DemandeItem
            key={d.id}
            d={d}
            open={openId === d.id}
            onToggle={() => setOpenId(openId === d.id ? null : d.id)}
            onTraite={(v) => mut.mutate({ id: d.id, traite: v })}
            busy={mut.isPending && mut.variables?.id === d.id}
          />
        ))}
        {filtered.length === 0 && (
          <p className="py-12 text-center text-sm text-ink/40">Aucune demande ne correspond.</p>
        )}
      </div>
    </div>
  )
}

function DemandeItem({
  d,
  open,
  onToggle,
  onTraite,
  busy,
}: {
  d: DemandeRow
  open: boolean
  onToggle: () => void
  onTraite: (v: boolean) => void
  busy: boolean
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-navy/10 bg-white">
      <button onClick={onToggle} className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-sand/60">
        <span
          className={`grid h-2.5 w-2.5 shrink-0 rounded-full ${d.traite ? 'bg-green-500' : 'bg-orange'}`}
          title={d.traite ? 'Traité' : 'À traiter'}
        />
        <div className="min-w-0 flex-1">
          <p className="truncate font-semibold text-navy">
            {d.nom || 'Sans nom'}{' '}
            <span className="font-normal text-ink/45">· {d.localite || '—'}</span>
          </p>
          <p className="text-xs text-ink/45">
            {d.numero_demande ? `N° ${d.numero_demande} · ` : ''}
            {formatDate(d.created_at)} · {d.machines.length} machine{d.machines.length > 1 ? 's' : ''}
          </p>
        </div>
        {d.traite && (
          <span className="hidden rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700 sm:inline">
            Traité
          </span>
        )}
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink/40 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="border-t border-navy/10 bg-sand/40 px-4 py-4 text-sm">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <p className="font-semibold text-navy">Coordonnées</p>
              {d.adresse && (
                <p className="flex items-start gap-2 text-ink/75">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ink/40" />
                  {d.adresse}, {d.code_postal} {d.localite}
                </p>
              )}
              {d.telephone1 && (
                <p className="flex items-center gap-2 text-ink/75">
                  <Phone className="h-4 w-4 shrink-0 text-ink/40" />
                  <a href={`tel:${d.telephone1}`} className="hover:text-orange">{d.telephone1}</a>
                  {d.telephone2 ? ` · ${d.telephone2}` : ''}
                </p>
              )}
              {d.email && (
                <p className="flex items-center gap-2 text-ink/75">
                  <Mail className="h-4 w-4 shrink-0 text-ink/40" />
                  <a href={`mailto:${d.email}`} className="break-all hover:text-orange">{d.email}</a>
                </p>
              )}
              {d.numero_client && <p className="text-ink/55">N° client : {d.numero_client}</p>}
            </div>

            <div className="space-y-1.5">
              <p className="font-semibold text-navy">Matériel</p>
              <ul className="space-y-1">
                {d.machines.map((m, i) => (
                  <li key={i} className="flex items-start gap-2 text-ink/75">
                    <Wrench className="mt-0.5 h-4 w-4 shrink-0 text-ink/40" />
                    <span>
                      {[m.machine, m.marque].filter(Boolean).join(' — ') || 'Machine'}
                      {m.remarque && m.remarque.toUpperCase() !== 'NON' ? (
                        <span className="text-ink/50"> · {m.remarque}</span>
                      ) : null}
                    </span>
                  </li>
                ))}
                {d.machines.length === 0 && <li className="text-ink/40">Aucune machine indiquée</li>}
              </ul>
            </div>
          </div>

          {d.remarque_generale && (
            <div className="mt-4">
              <p className="font-semibold text-navy">Remarque</p>
              <p className="mt-1 whitespace-pre-line text-ink/75">{d.remarque_generale}</p>
            </div>
          )}

          <div className="mt-4">
            <button
              onClick={() => onTraite(!d.traite)}
              disabled={busy}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
                d.traite
                  ? 'bg-white text-ink/70 ring-1 ring-navy/15 hover:bg-sand'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {d.traite ? 'Marquer comme à traiter' : 'Marquer comme traité'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
